import { useAtom } from "@dbeining/react-atom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ErrorMessage, Field, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, FormCheck, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import { userAtom } from "../../atoms/userAtom";
import { LoadingSpinner } from "../LoadingSpinner";
import { userService } from "../../services";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import { showToastSuccess, showToastError } from "../../helpers";
import { IPersonnelGroup } from "../../customTypings/PersonnelGroup";
import { personnelGroupService } from "../../services/personnelgroup.service";
dayjs.extend(utc);


type Props = {
    customerId: string | undefined | null;
    show: boolean;
    onClose: (added: boolean) => void;
    groupId?: string | undefined;
    location?: string;
};

const EditPersonnelGroupModel: React.FC<Props> = ({ customerId, show, onClose, groupId = "", location = "" }) => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [currentGroup, setCurrentGroup] = useState<IPersonnelGroup>();
    const [allUsers, setAllUsers] = useState<any[]>();
    const [loading, setLoading] = useState(true);

    const handleClose = () => onClose(false);
    const currentUser = useAtom(userAtom);

    useEffect(() => {
        Promise.all([userService.getAllListItems()]).then((response) => {
            setAllUsers(response[0]);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (show) {
            setLoading(true);
            personnelGroupService.get(groupId).then((group) => {
                setCurrentGroup(group);
                setLoading(false);
            });
        }
    }, [groupId, show]);

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose} size={undefined}>
            <Formik
                initialValues={{
                    userIds: currentGroup?.userIds as string[],
                    name: currentGroup?.name
                }}
                onSubmit={(values, { setStatus, setSubmitting, setFieldError }) => {
                    setStatus();
                    setSubmitting(true);

                    const newTask: IPersonnelGroup = {
                        id: currentGroup?.id,
                        userIds: values.userIds,
                        name: values.name || currentGroup?.name || ""
                    };
                    personnelGroupService.editPersonnelGroup(newTask).then(
                        async (response: any) => {
                            setSubmitting(false);
                            if (response.status !== "Failure") {
                                showToastSuccess("Personnel group updated successfully!");
                                onClose(true);
                            } else {
                                setAlertVariant("danger");
                                showToastError(response.message);
                            }
                        },
                        (error: any) => {
                            setAlertVariant("danger");
                            if (error.status === 400 && error.errors) {
                                Object.keys(error.errors).forEach((key) => {
                                    const messages = error.errors[key];
                                    if (Array.isArray(messages)) {
                                        messages.forEach((message) => showToastError(message));
                                    }
                                });
                            } else {
                                showToastError("An unexpected error occurred!");
                            }
                            setSubmitting(false);
                        }
                    );

                }}
            >
                {({ errors, status, touched, isSubmitting, handleSubmit, values, handleChange, setFieldValue }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Personnel Group</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="w-100">
                            <Form.Group as={Row} className="mb-3" controlId="name">
                                <Form.Label column sm={3}>
                                    Group Name <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col sm={9}>
                                    <Field name="name" type="text" className={"form-control" + (errors.name && touched.name ? " is-invalid" : "")} value={ currentGroup?.name } />
                                    <ErrorMessage name="name" component="div" className="invalid-feedback" />
                                </Col>
                            </Form.Group>
                                    <Form.Group as={Row} className="mb-3" controlId="userIds">
                                        <Form.Label column sm={3}>
                                            Personnel <span style={{ color: 'red' }}>*</span>
                                        </Form.Label>
                                        <Col sm={9}>
                                            <Select
                                                isMulti
                                                styles={ReactSelectBootstrapStyle}
                                                className="stateManagedSelect"
                                                onChange={(value) => {
                                                    var identityIds: string[] = [];
                                                    for (let i = 0; i < value.length; i++) {
                                                        identityIds.push(value[i].value);
                                                    };
                                                    setFieldValue("userIds", identityIds);
                                                }}
                                                defaultValue={allUsers?.filter((obj) =>
                                                    currentGroup?.userIds?.includes(obj.value)
                                                )}
                                                isClearable
                                                options={allUsers}
                                            />
                                            <ErrorMessage name="userIds" component="div" className="invalid-feedback" />
                                        </Col>
                                    </Form.Group>
                            {status && (
                                <Alert variant={alertVariant} className="mt-3">
                                    {status}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div className="form-group">
                                    <Button variant="primary" disabled={isSubmitting} type="submit" className="me-2">
                                        {isSubmitting ? <LoadingSpinner text="Updating group..." /> : "Update Group"}
                                    </Button>
                                    <Button variant="secondary" onClick={handleClose}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        </Modal.Footer>

                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export { EditPersonnelGroupModel };
